import { createContext, FC, PropsWithChildren, useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { getVirtualPageViewEventDataObject } from 'utils/gtm/virtualPageView'
import { OsContextValue } from '@wppopen/react'

interface NavigationContextType {
  previousPath: string | null
  currentPath: string
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined)

// Paths that should be skipped in the navigation history
const TRANSIENT_PATHS = ['/settings']

export const useNavigation = () => {
  const context = useContext(NavigationContext)
  if (!context) {
    throw new Error('useNavigationContext must be used within a NavigationProvider')
  }
  return context
}

interface NavigationContextProviderProps extends PropsWithChildren {
  osContextValue: OsContextValue | null
}

export const NavigationProvider: FC<NavigationContextProviderProps> = ({ osContextValue, children }) => {
  const { pathname } = useLocation()
  const [navigationState, setNavigationState] = useState<NavigationContextType>({
    previousPath: null,
    currentPath: pathname,
  })

  // Keep a history of relevant paths
  const pathHistoryStackRef = useRef<string[]>([navigationState.currentPath])
  // Track if initial page view has been sent
  const initialPageViewSent = useRef(false)

  // Handle navigation state updates
  useEffect(() => {
    const currentPath = pathname
    if (currentPath === navigationState.currentPath) return

    const isTransient = TRANSIENT_PATHS.includes(currentPath)

    if (!isTransient) {
      pathHistoryStackRef.current.push(currentPath)
      // Keep limited history to the last 10 paths
      if (pathHistoryStackRef.current.length > 10) {
        pathHistoryStackRef.current.shift()
      }
    }

    const historyStackLength = pathHistoryStackRef.current.length

    const previousPath =
      historyStackLength > 1 ? pathHistoryStackRef.current[historyStackLength - 2] : navigationState.previousPath

    setNavigationState({
      previousPath,
      currentPath,
    })
  }, [pathname])

  const firePageViewEvent = () => {
    const pageViewEventData = getVirtualPageViewEventDataObject(osContextValue!, {
      pageUrl: navigationState.currentPath,
      pageTitle: document.title,
      previousUrl: navigationState.previousPath || '',
    })
    window.wppopen_dataLayer.push(pageViewEventData)
  }

  useEffect(() => {
    const { currentPath, previousPath } = navigationState
    if (
      !osContextValue ||
      TRANSIENT_PATHS.includes(currentPath) ||
      (!!initialPageViewSent.current && currentPath === previousPath)
    )
      return
    firePageViewEvent()

    if (!initialPageViewSent.current) {
      initialPageViewSent.current = true
    }
  }, [navigationState.currentPath, osContextValue])

  return <NavigationContext.Provider value={navigationState}>{children}</NavigationContext.Provider>
}
